import React from "react";
import {
  Badge,
  Box,
  Circle,
  Flex,
  HStack,
  Text,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { BsCheck2, BsDash, BsExclamationTriangle } from "react-icons/bs";
import StatusBadge from "../common/StatusBadge";
import { formatDate } from "../../utils/normalize";

const stateColor = (status, isCurrent) => {
  const normalized = String(status || "").toUpperCase();
  if (normalized === "COMPLETED" || normalized === "DONE") return "green";
  if (normalized === "SENT_BACK" || normalized === "REJECTED") return "orange";
  if (normalized === "SKIPPED" || normalized === "NOT_REQUIRED") return "gray";
  if (isCurrent || normalized === "CURRENT" || normalized === "IN_PROGRESS") return "blue";
  return "gray";
};

const stateIcon = (status, isCurrent) => {
  const normalized = String(status || "").toUpperCase();
  if (normalized === "COMPLETED" || normalized === "DONE") return BsCheck2;
  if (normalized === "SENT_BACK" || normalized === "REJECTED") return BsExclamationTriangle;
  if (isCurrent || normalized === "CURRENT" || normalized === "IN_PROGRESS") return BsDash;
  return BsDash;
};

const defaultStages = [
  { name: "Self-assessment", status: "COMPLETED", actor: "Employee" },
  { name: "Manager review", status: "CURRENT", actor: "Manager" },
  { name: "Calibration", status: "UPCOMING", actor: "HR" },
  { name: "Acknowledgement", status: "UPCOMING", actor: "Employee" },
];

const stageKey = (value) => {
  const key = String(value || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (["SELFASSESSMENT", "SELFASSESSMENTINPROGRESS", "DRAFT", "NOTSTARTED"].includes(key)) {
    return "SELFASSESSMENT";
  }
  if (["MANAGERREVIEW", "MANAGERASSESSMENT", "MANAGERREVIEWINPROGRESS"].includes(key)) {
    return "MANAGERREVIEW";
  }
  if (["CALIBRATION", "PENDINGCALIBRATION", "CALIBRATED"].includes(key)) {
    return "CALIBRATION";
  }
  if (["ACKNOWLEDGEMENT", "PENDINGACKNOWLEDGEMENT", "ACKNOWLEDGED", "FINALIZED"].includes(key)) {
    return "ACKNOWLEDGEMENT";
  }
  if (["COMPLETED", "CLOSED"].includes(key)) {
    return "COMPLETED";
  }
  return key;
};

const normalizeStages = (stages = [], currentStage) => {
  const rows = Array.isArray(stages) && stages.length ? stages : defaultStages;
  const currentStageKey = stageKey(currentStage);
  const isComplete = currentStageKey === "COMPLETED";
  const currentIndex = rows.findIndex((stage) =>
    stageKey(stage.name || stage.label || stage.stageName || stage.stage) === currentStageKey,
  );
  const fallbackCurrentIndex = rows.findIndex((stage) => stage.isCurrent);
  const resolvedCurrentIndex = isComplete
    ? -1
    : currentIndex >= 0
      ? currentIndex
      : fallbackCurrentIndex;

  return rows.map((stage, index) => {
    const name = stage.name || stage.label || stage.stageName || stage.stage || `Stage ${index + 1}`;
    const isCurrent = !isComplete && (resolvedCurrentIndex >= 0
      ? index === resolvedCurrentIndex
      : Boolean(stage.isCurrent));
    const derivedStatus = isComplete || isCurrent
      ? "CURRENT"
      : resolvedCurrentIndex >= 0 && index < resolvedCurrentIndex
        ? "COMPLETED"
        : "UPCOMING";
    return {
      id: stage.id || stage._id || name,
      name,
      status: isComplete
        ? "COMPLETED"
        : resolvedCurrentIndex >= 0
          ? derivedStatus
          : stage.status || stage.state || derivedStatus,
      actor: stage.actor || stage.assignedActor || stage.assigneeRole,
      completedAt: stage.completedAt || stage.completedOn,
      isCurrent,
    };
  });
};

const ReviewProgressTracker = ({ stages, currentStage }) => {
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const mutedText = useColorModeValue("gray.600", "gray.400");
  const normalizedStages = normalizeStages(stages, currentStage);

  return (
    <Flex
      direction={{ base: "column", md: "row" }}
      gap={{ base: 3, md: 0 }}
      align={{ base: "stretch", md: "flex-start" }}
    >
      {normalizedStages.map((stage, index) => {
        const color = stateColor(stage.status, stage.isCurrent);
        const Icon = stateIcon(stage.status, stage.isCurrent);
        return (
          <Flex
            key={stage.id}
            flex="1"
            gap={3}
            position="relative"
            pb={{ base: index === normalizedStages.length - 1 ? 0 : 3, md: 0 }}
          >
            <VStack spacing={1} align="center">
              <Circle size="28px" bg={`${color}.500`} color="white">
                <Icon />
              </Circle>
              {index < normalizedStages.length - 1 ? (
                <Box
                  flex="1"
                  minH={{ base: "26px", md: 0 }}
                  w={{ base: "1px", md: "100%" }}
                  borderColor={borderColor}
                  borderLeftWidth={{ base: "1px", md: 0 }}
                  borderTopWidth={{ base: 0, md: "1px" }}
                />
              ) : null}
            </VStack>
            <Box pr={{ base: 0, md: 4 }}>
              <HStack spacing={2} wrap="wrap">
                <Text fontSize="sm" fontWeight="semibold">
                  {stage.name}
                </Text>
                {stage.isCurrent ? <Badge colorScheme="blue">Current</Badge> : null}
              </HStack>
              <HStack spacing={2} mt={1} wrap="wrap">
                <StatusBadge value={stage.status} />
                {stage.actor ? (
                  <Text fontSize="xs" color={mutedText}>
                    {stage.actor}
                  </Text>
                ) : null}
              </HStack>
              {stage.completedAt ? (
                <Text mt={1} fontSize="xs" color={mutedText}>
                  Completed {formatDate(stage.completedAt)}
                </Text>
              ) : null}
            </Box>
          </Flex>
        );
      })}
    </Flex>
  );
};

export default ReviewProgressTracker;
